/**
 * Chamadas HTTP do caixa da casa.
 *
 * Papel: aqui ficam as chamadas de `/api/financas` que a tela usa — o caixa, o
 * Fechamento, a listagem, a gravação pelo modal, o lote, a reordenação, o
 * plano de contas, os padrões de favorecido e os favorecidos.
 *
 * Como usar: as respostas já chegam prontas para a tela — a listagem na ordem
 * da tabela e com o saldo corrido, o Fechamento com as três tabelas agregadas,
 * o plano e os favorecidos em ordem alfabética —, e reordenar ou somar aqui
 * desfaria a regra que a API aplicou. As rotas que respondem 204 sem corpo
 * passam por `clienteApi.remover` ou `clienteApi.enviar`, que não leem
 * resposta.
 */
import {
  type Caixa,
  type Categoria,
  type ConsultaDoFechamento,
  type Favorecido,
  type Fechamento,
  type FiltroDeLancamentos,
  type Lancamento,
  type ListagemDeLancamentos,
  type LoteDeLancamentos,
  type NovaCategoria,
  type NovoFavorecido,
  type PadraoDeFavorecido,
  type PerfilDoFavorecido,
  type ReordenarLancamento,
  type ResultadoDasSubcategorias,
  type ResultadoDoLote,
  type SalvarLancamento,
  caixaSchema,
  categoriaSchema,
  favorecidoSchema,
  favorecidosSchema,
  fechamentoSchema,
  lancamentosGravadosSchema,
  listagemDeLancamentosSchema,
  padroesDeFavorecidoSchema,
  planoDeContasSchema,
  resultadoDasSubcategoriasSchema,
  resultadoDoLoteSchema,
} from '@concierge/shared';

import { clienteApi } from '@/shared/api';

/**
 * Lê a casa do caixa, o mês-base e o Saldo Atual.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns O caixa; `residencia` nula quando nenhuma casa tem caixa.
 */
export function buscarCaixa(sinal?: AbortSignal): Promise<Caixa> {
  return clienteApi.get('/financas/caixa', { schema: caixaSchema, sinal });
}

/**
 * Lê o Fechamento de um mês.
 *
 * @param consulta - A casa do caixa e o mês aberto.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns Os KPIs, as três tabelas e o gráfico trimestral do mês.
 */
export function buscarFechamento(
  consulta: ConsultaDoFechamento,
  sinal?: AbortSignal,
): Promise<Fechamento> {
  return clienteApi.get('/financas/fechamento', {
    schema: fechamentoSchema,
    params: { residenciaId: consulta.residenciaId, competencia: consulta.competencia },
    sinal,
  });
}

/**
 * Lê as linhas da tabela de lançamentos.
 *
 * Os filtros ausentes não entram na URL, e a API decide a fonte: com pesquisa
 * ou período, o caixa inteiro; sem eles, o mês de `competencia`.
 *
 * @param filtro - A casa, o mês aberto e os filtros da barra.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As linhas na ordem da tabela, com o saldo corrido, e a soma do rodapé.
 */
export function buscarLancamentos(
  filtro: FiltroDeLancamentos,
  sinal?: AbortSignal,
): Promise<ListagemDeLancamentos> {
  return clienteApi.get('/financas/lancamentos', {
    schema: listagemDeLancamentosSchema,
    params: {
      residenciaId: filtro.residenciaId,
      competencia: filtro.competencia,
      pesquisa: filtro.pesquisa,
      de: filtro.de,
      ate: filtro.ate,
      tipo: filtro.tipo,
      categoria: filtro.categoria,
      status: filtro.status,
      forma: filtro.forma,
    },
    sinal,
  });
}

/**
 * Cria um lançamento pelo modal, com a recorrência ou o parcelamento pedido.
 *
 * @param corpo - O lançamento e a repetição, já validados.
 * @returns As linhas gravadas, na ordem em que nasceram.
 */
export function criarLancamento(corpo: SalvarLancamento): Promise<Lancamento[]> {
  return clienteApi.post('/financas/lancamentos', corpo, {
    schema: lancamentosGravadosSchema,
  });
}

/**
 * Edita um lançamento pendente pelo modal.
 *
 * A API recusa com 409 o lançamento que já está pago, e a mensagem é a da
 * origem.
 *
 * @param id - Identificador do lançamento editado.
 * @param corpo - O lançamento e a repetição, já validados.
 * @returns As linhas gravadas: o editado primeiro, as cópias ou parcelas depois.
 */
export function atualizarLancamento(
  id: string,
  corpo: SalvarLancamento,
): Promise<Lancamento[]> {
  return clienteApi.put(`/financas/lancamentos/${encodeURIComponent(id)}`, corpo, {
    schema: lancamentosGravadosSchema,
  });
}

/**
 * Exclui um lançamento, em qualquer mês.
 *
 * @param id - Identificador do lançamento.
 */
export function excluirLancamento(id: string): Promise<void> {
  return clienteApi.remover(`/financas/lancamentos/${encodeURIComponent(id)}`);
}

/**
 * Aplica uma das cinco ações da barra de lote, pagos inclusive.
 *
 * É também o caminho do ✓ e do selo clicável da linha, com um `id` só.
 *
 * @param lote - A ação e os identificadores.
 * @returns Quantos lançamentos existiam e foram alterados ou excluídos.
 */
export function aplicarLote(lote: LoteDeLancamentos): Promise<ResultadoDoLote> {
  return clienteApi.post('/financas/lancamentos/lote', lote, {
    schema: resultadoDoLoteSchema,
  });
}

/**
 * Move um lançamento para antes ou depois de outro do mesmo mês.
 *
 * @param corpo - Quem foi solto, sobre quem, e se depois dele.
 */
export function reordenarLancamento(corpo: ReordenarLancamento): Promise<void> {
  return clienteApi.enviar('POST', '/financas/lancamentos/reordenar', corpo);
}

/**
 * Lê o plano de contas inteiro.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As categorias em ordem alfabética, cada uma com as subcategorias.
 */
export function buscarPlanoDeContas(sinal?: AbortSignal): Promise<Categoria[]> {
  return clienteApi.get('/financas/plano-de-contas', { schema: planoDeContasSchema, sinal });
}

/**
 * Cria uma categoria de despesa, com as subcategorias das fichas.
 *
 * @param corpo - O nome, a cor e as subcategorias.
 * @returns A categoria criada.
 */
export function criarCategoria(corpo: NovaCategoria): Promise<Categoria> {
  return clienteApi.post('/financas/categorias', corpo, { schema: categoriaSchema });
}

/**
 * Grava a cor de uma categoria.
 *
 * @param id - Identificador da categoria.
 * @param cor - A cor em `#rrggbb`.
 */
export function mudarCorDaCategoria(id: string, cor: string): Promise<void> {
  return clienteApi.enviar('PUT', `/financas/categorias/${encodeURIComponent(id)}/cor`, {
    cor,
  });
}

/**
 * Exclui uma categoria que não é de sistema nem está em uso.
 *
 * @param id - Identificador da categoria.
 */
export function excluirCategoria(id: string): Promise<void> {
  return clienteApi.remover(`/financas/categorias/${encodeURIComponent(id)}`);
}

/**
 * Acrescenta subcategorias a uma categoria; as que já existem são ignoradas.
 *
 * @param categoriaId - Identificador da categoria.
 * @param nomes - Os nomes das fichas, ao menos um.
 * @returns Quantas entraram.
 */
export function criarSubcategorias(
  categoriaId: string,
  nomes: readonly string[],
): Promise<ResultadoDasSubcategorias> {
  return clienteApi.post(
    `/financas/categorias/${encodeURIComponent(categoriaId)}/subcategorias`,
    { nomes },
    { schema: resultadoDasSubcategoriasSchema },
  );
}

/**
 * Exclui uma subcategoria que não é de sistema nem está em uso.
 *
 * @param id - Identificador da subcategoria.
 */
export function excluirSubcategoria(id: string): Promise<void> {
  return clienteApi.remover(`/financas/subcategorias/${encodeURIComponent(id)}`);
}

/**
 * Lê os padrões de favorecido, para o autopreenchimento e a lista do modal.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns Os padrões.
 */
export function buscarPadroesDeFavorecido(sinal?: AbortSignal): Promise<PadraoDeFavorecido[]> {
  return clienteApi.get('/financas/padroes-de-favorecido', {
    schema: padroesDeFavorecidoSchema,
    sinal,
  });
}

/**
 * Lê os favorecidos da casa do caixa.
 *
 * @param residenciaId - A casa do caixa.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns Os favorecidos em ordem alfabética, com `emUso` e a ajuda de custo.
 */
export function buscarFavorecidos(
  residenciaId: string,
  sinal?: AbortSignal,
): Promise<Favorecido[]> {
  return clienteApi.get('/financas/favorecidos', {
    schema: favorecidosSchema,
    params: { residenciaId },
    sinal,
  });
}

/**
 * Cadastra um favorecido só com o nome, herdando o padrão de mesmo nome.
 *
 * @param corpo - A casa e o nome.
 * @returns O favorecido criado; 409 quando o nome já existe na casa.
 */
export function criarFavorecido(corpo: NovoFavorecido): Promise<Favorecido> {
  return clienteApi.post('/financas/favorecidos', corpo, { schema: favorecidoSchema });
}

/**
 * Grava o perfil de um favorecido.
 *
 * @param id - Identificador do favorecido.
 * @param perfil - O perfil, já limpo por `perfilDoFavorecidoSchema`.
 * @returns O favorecido como ficou gravado.
 */
export function salvarPerfilDoFavorecido(
  id: string,
  perfil: PerfilDoFavorecido,
): Promise<Favorecido> {
  return clienteApi.put(`/financas/favorecidos/${encodeURIComponent(id)}`, perfil, {
    schema: favorecidoSchema,
  });
}

/**
 * Exclui um favorecido que não está em uso.
 *
 * @param id - Identificador do favorecido.
 */
export function excluirFavorecido(id: string): Promise<void> {
  return clienteApi.remover(`/financas/favorecidos/${encodeURIComponent(id)}`);
}
